import React, { useState, useEffect, useRef } from 'react';
import { API_URLS } from '../config';
import './styles/BusinessSettings.css';

// BusinessSettings.js

const BusinessSettings = ({
  businessId,
  setLogoUrl: parentSetLogoUrl,
  setHeaderUrl: parentSetHeaderUrl,
  setBusinessName: parentSetBusinessName, // Update parent's live name state
  businessName, // RENT NAME FROM PARENT'S INPUT STATE
  onSettingsChange,
  initialLogoUrl,
  initialHeaderUrl,
  initialBusinessNameFromServer,
}) => {
  console.log("Business name from parent:", businessName);
  const [headerImage, setHeaderImage] = useState(null); // File object for new upload
  const [logoImage, setLogoImage] = useState(null); // File object for new upload
  const [newHeaderFile, setNewHeaderFile] = useState(null);
  const [newLogoFile, setNewLogoFile] = useState(null);
  const [headerPreview, setHeaderPreview] = useState(initialHeaderUrl || null);
  const [logoPreview, setLogoPreview] = useState(initialLogoUrl || null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [finalBusinessName, setFinalBusinessName] = useState(businessName);


  const committedBusinessName = useRef(businessName); // Use initial prop
  const committedHeaderUrl = useRef(initialHeaderUrl || null);
  const committedLogoUrl = useRef(initialLogoUrl || null);

  // Update committed values if the underlying business or its initial props change
  useEffect(() => {
    console.log("EFFECT: Resetting committed values due to prop changes.");
    console.log("  Initial Name from Server Prop:", businessName);
    console.log("  Initial Header URL Prop:", initialHeaderUrl);
    console.log("  Initial Logo URL Prop:", initialLogoUrl);

    committedBusinessName.current = businessName;
    committedHeaderUrl.current = initialHeaderUrl || null;
    committedLogoUrl.current = initialLogoUrl || null;

    // Reset previews and staged files to match the new initial state
    setHeaderPreview(initialHeaderUrl || null);
    setLogoPreview(initialLogoUrl || null);
    setNewHeaderFile(null);
    setNewLogoFile(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, businessName, initialHeaderUrl, initialLogoUrl]);


  const handleImageUpload = (field, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result; // Data URL
        if (field === 'headerImage') {
          setHeaderImage(file);
          setHeaderPreview(result);
          parentSetHeaderUrl(result);
        } else if (field === 'logoImage') {
          setLogoImage(file);
          setLogoPreview(result);
          parentSetLogoUrl(result);
        }
        if (onSettingsChange) {
          onSettingsChange(); // Trigger preview refresh
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = (type) => {
    if (type === 'header') {
      setHeaderImage(null);
      setHeaderPreview(null);
      parentSetHeaderUrl(null);
    } else if (type === 'logo') {
      setLogoImage(null);
      setLogoPreview(null);
      parentSetLogoUrl(null);
    }
    if (onSettingsChange) {
      onSettingsChange();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSubmitting(true);
    console.log("--- Submit Start ---");
    console.log("Initial Refs:", { name: committedBusinessName.current, header: committedHeaderUrl.current, logo: committedLogoUrl.current });
    console.log("Current State:", { name: businessName, headerFile: headerImage, headerPreview: headerPreview, logoFile: logoImage, logoPreview: logoPreview });


    const token = localStorage.getItem('token');
    if (!token || !businessId) {
      setError("Authentication token or Business ID is missing.");
      setIsSubmitting(false);
      console.error("Auth token or Business ID missing.");
      return;
    }

    // --- Determine Changes ---
    const infoPayload = {};
    let hasImageOperation = false;

    // Check if business name changed
    // `businessName` (prop) is the current value from the input.
    // `committedBusinessName.current` is the value from initialBusinessNameFromServer prop.
    if (finalBusinessName !== committedBusinessName.current) {
      infoPayload.businessName = finalBusinessName;
      console.log(`Change detected: Business name. Was: "${committedBusinessName.current}", Is: "${finalBusinessName}"`);
    }

    // Check header image changes
    const newHeaderFile = headerImage instanceof File;
    // Explicitly check if preview is null now AND initial preview was not null AND no new file is staged
    const headerRemoved = headerPreview === null && committedHeaderUrl.current !== null && !newHeaderFile;

    if (newHeaderFile) {
        // Use headerPreview which contains the data URL instead of the File object
        infoPayload.headerImage = headerPreview; // Send base64 data URL like BusinessDetailsStep
        hasImageOperation = true;
        console.log("Change detected: New header file");
    } else if (headerRemoved) {
        infoPayload.headerImageRemoved = true;
        hasImageOperation = true;
        console.log("Change detected: Header image removed");
    }

    // Check logo image changes
    const newLogoFile = logoImage instanceof File;
    // Explicitly check if preview is null now AND initial preview was not null AND no new file is staged
    const logoRemoved = logoPreview === null && committedLogoUrl.current !== null && !newLogoFile;

     if (newLogoFile) {
        // Use logoPreview which contains the data URL instead of the File object
        infoPayload.logoImage = logoPreview; // Send base64 data URL like BusinessDetailsStep
        hasImageOperation = true;
        console.log("Change detected: New logo file");
    } else if (logoRemoved) {
        infoPayload.logoImageRemoved = true;
        hasImageOperation = true;
        console.log("Change detected: Logo image removed");
    }
    // --- End Determine Changes ---

    // If nothing changed, do not send request
    if (Object.keys(infoPayload).length === 0) {
      setError('No changes detected.');
      setIsSubmitting(false);
      console.log("Result: No changes detected in payload.");
      console.log("--- Submit End (No Changes) ---");
      return;
    }

    console.log("Payload to send:", infoPayload);
    console.log("Requires image operation handling:", hasImageOperation);

    try {
      // CHANGE: Always use JSON approach since we're sending base64 strings, not files
      const fetchOptions = {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(infoPayload)
      };

      console.log("Sending Fetch Request with options:", { 
        method: fetchOptions.method, 
        headers: fetchOptions.headers, 
        bodyType: 'JSON'
      });

      const infoResponse = await fetch(API_URLS.businessInfo(businessId), fetchOptions);
      const infoData = await infoResponse.json().catch(() => ({ message: 'Response was not valid JSON', status: infoResponse.status })); // Graceful JSON parsing

      console.log("Received Response Status:", infoResponse.status);
      console.log("Received Response Body:", infoData);


      if (!infoResponse.ok) {
        throw new Error(infoData.message || `HTTP error ${infoResponse.status}`);
      }

      setSuccessMessage('Business information updated successfully.');
      console.log("Update successful.");

      // --- Update Refs and State Post-Success ---
      console.log("Updating internal state after success...");
      if ('businessName' in infoPayload) {
          committedBusinessName.current = businessName; // Update committed ref to the new saved name
          // Parent should also update its `initialBusinessNameFromServer` state if it re-fetches or gets data from response
          console.log("  > Updated initialBusinessName ref:", committedBusinessName.current);
          parentSetBusinessName(finalBusinessName); // Update local state to match the new name
          localStorage.setItem('businessName', finalBusinessName); // Update local storage
      }
      // Update image refs based on successful operations
      if (newHeaderFile) { // Use the flag determined earlier
          committedHeaderUrl.current = headerPreview; // Update ref to new preview URL
          console.log("  > Updated initialHeaderPreview ref (new image)");
      } else if (headerRemoved) { // Use the flag determined earlier
          committedHeaderUrl.current = null; // Update ref to reflect removal
          console.log("  > Updated initialHeaderPreview ref (removed)");
      }
      if (newLogoFile) { // Use the flag determined earlier
          committedLogoUrl.current = logoPreview; // Update ref to new preview URL
           console.log("  > Updated initialLogoPreview ref (new image)");
      } else if (logoRemoved) { // Use the flag determined earlier
          committedLogoUrl.current = null; // Update ref to reflect removal
           console.log("  > Updated initialLogoPreview ref (removed)");
      }

      // Reset file input states only after successful processing
      setHeaderImage(null);
      setLogoImage(null);
      console.log("  > Reset local file states (headerImage, logoImage) to null");
      // --- End Update Refs and State ---


    } catch (error) {
      console.error('Failed to update business information:', error);
      setError(`Error updating information: ${error.message}.`);
      // Do not reset previews or refs on error
    } finally {
        setIsSubmitting(false);
        console.log("--- Submit End ---");
    }
  };

  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  return (
    <form className="business-settings-form" onSubmit={handleSubmit}>
      <h3>Business Details</h3>
      
      {error && <p className="error-message">{error}</p>}
      {successMessage && <p className="success-message">{successMessage}</p>}
      
      <div className="business-form-section">
        <h4 className="business-section-title">Company Information</h4>
        <div className="business-form-group">
          <label htmlFor="businessNameInput">Business Name:</label>
          <input
            id="businessNameInput"
            className="business-form-control"
            type="text"
            value={finalBusinessName || ''}
            onChange={(e) => setFinalBusinessName(e.target.value)}
            required
            placeholder="Enter your business name"
          />
        </div>
      </div>
      
      <div className="business-form-section">
        <h4 className="business-section-title">Company Branding</h4>
        
        <div className="image-upload-container">
          <label className="image-upload-label">Header Image:</label>
          <div className={`image-upload-preview ${headerPreview ? 'with-image' : ''}`}>
            {headerPreview ? (
              <>
                <img src={headerPreview} alt="Header Preview" />
                <div className="image-actions">
                  <button 
                    type="button" 
                    className="remove-image-btn"
                    onClick={() => handleRemoveImage('header')}
                  >
                    Remove
                  </button>
                </div>
              </>
            ) : (
              <div className="image-upload-placeholder">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p>No header image selected</p>
                <label className="upload-button">
                  <svg xmlns="http://www.w3.org/2000/svg" className="upload-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload Header Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('headerImage', e.target.files[0])}
                  />
                </label>
              </div>
            )}
          </div>
        </div>
        
        <div className="image-upload-container">
          <label className="image-upload-label">Logo Image:</label>
          <div className={`image-upload-preview ${logoPreview ? 'with-image' : ''}`}>
            {logoPreview ? (
              <>
                <img src={logoPreview} alt="Logo Preview" />
                <div className="image-actions">
                  <button 
                    type="button" 
                    className="remove-image-btn"
                    onClick={() => handleRemoveImage('logo')}
                  >
                    Remove
                  </button>
                </div>
              </>
            ) : (
              <div className="image-upload-placeholder">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p>No logo image selected</p>
                <label className="upload-button">
                  <svg xmlns="http://www.w3.org/2000/svg" className="upload-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload Logo Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('logoImage', e.target.files[0])}
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <button 
        className="business-submit-button" 
        type="submit"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <span className="loading-spinner"></span>
            Saving...
          </>
        ) : 'Save Changes'}
      </button>
    </form>
  );
};

export default BusinessSettings;